import { Component } from "@theme/component";
import { ThemeEvents, VariantUpdateEvent } from "@theme/events";

/**
 * A custom element that swaps the product description when the selected variant changes.
 *
 * When a variant:update event fires, this component finds the matching
 * <product-variant-description> in the re-rendered section HTML and replaces
 * its own inner content. The server-side Liquid in product-description.liquid
 * already resolves variant metafield vs default description, so this JS only
 * needs to transplant the new HTML — no extra fetch required.
 *
 * @extends {Component}
 */
class ProductVariantDescription extends Component {
  connectedCallback() {
    super.connectedCallback();
    const target = this.closest(
      '.shopify-section, dialog, [id*="ProductInformation-"], [id*="QuickAdd-"]',
    );
    if (!target) return;
    target.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest(
      '.shopify-section, dialog, [id*="ProductInformation-"], [id*="QuickAdd-"]',
    );
    if (!target) return;
    target.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
    );
  }

  /**
   * Handles the variant:update event.
   * Finds the updated product-variant-description element in the new HTML
   * and swaps this element's inner content to match.
   *
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    // Handle combined listings (product swaps)
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const html = event.detail.data.html;
    if (!html) return;

    // Find the matching element in the re-rendered section HTML
    const newDescriptionEl = html.querySelector(
      `product-variant-description[data-block-id="${this.dataset.blockId}"]`,
    );

    if (!newDescriptionEl) return;

    // Replace inner content (the text block rendered by snippets/text.liquid)
    this.innerHTML = newDescriptionEl.innerHTML;
  };
}

if (!customElements.get("product-variant-description")) {
  customElements.define(
    "product-variant-description",
    ProductVariantDescription,
  );
}
